import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { CompositeCommand } from 'editor/models/command';
import type { MidiEvent } from 'editor/models/song';
import { UpdateMidiEventVelocityCommand } from 'editor/models/song-commands';
import type UndoManagerService from 'editor/services/undo-manager';
import constrainValue from 'editor/utils/constrain-value';

interface VelocityValuesSignature {
  Element: HTMLDivElement;
  Args: {
    events: MidiEvent[];
    defaultValue: number;
    // eslint-disable-next-line no-unused-vars
    setDefault: (value: number) => void;
  };
  Blocks: {
    // default: [];
  };
}

export default class VelocityValues extends Component<VelocityValuesSignature> {
  @service declare undoManager: UndoManagerService;

  constructor(owner: unknown, args: VelocityValuesSignature['Args']) {
    super(owner, args);
  }

  get events() {
    return this.args.events;
  }

  get hasEvents() {
    return this.events.length > 0;
  }

  get hasMultipleEvents() {
    return this.events.length > 1 && this.minValue !== this.maxValue;
  }

  get minValue() {
    return this.events.reduce((min, event) => Math.min(min, event.velocity), 127);
  }

  get maxValue() {
    return this.events.reduce((max, event) => Math.max(max, event.velocity), 0);
  }

  @action
  updateMinValue(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const oldMin = this.minValue;
    const oldMax = this.maxValue;
    let newMin = parseInt(input.value, 10);
    if (isNaN(newMin)) {
      input.value = String(oldMin);
      return;
    }
    if (newMin == oldMin) {
      return;
    }

    let commands: UpdateMidiEventVelocityCommand[] = [];
    if (this.events.length === 1 || this.minValue === this.maxValue) {
      const newVelocity = newMin;
      this.events.forEach((event) => {
        commands.push(
          new UpdateMidiEventVelocityCommand(event, event.velocity, newVelocity),
        );
      });
    } else {
      this.events.forEach((event) => {
        const newVelocity = Math.min(
          127,
          Math.round(
            ((event.velocity - oldMin) / (oldMax - oldMin)) * (oldMax - newMin) + newMin,
          ),
        );
        commands.push(
          new UpdateMidiEventVelocityCommand(event, event.velocity, newVelocity),
        );
      });
    }

    this.undoManager.executeCommand(new CompositeCommand(commands[0]!.name, commands));
  }

  @action
  updateMaxValue(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const oldMin = this.minValue;
    const oldMax = this.maxValue;
    let newMax = parseInt(input.value, 10);
    if (isNaN(newMax)) {
      input.value = String(oldMax);
      return;
    }
    if (newMax == oldMax) {
      return;
    }
    const commands = this.events.map((event) => {
      const newVelocity = Math.min(
        127,
        Math.round(
          ((event.velocity - oldMin) / (oldMax - oldMin)) * (newMax - oldMin) + oldMin,
        ),
      );
      return new UpdateMidiEventVelocityCommand(event, event.velocity, newVelocity);
    });

    this.undoManager.executeCommand(new CompositeCommand(commands[0]!.name, commands));
  }

  @action
  setDefaultValue(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const newDefault = parseInt(input.value, 10);
    if (isNaN(newDefault)) {
      return;
    }
    this.args.setDefault(constrainValue(newDefault, 0, 127));
  }

  <template>
    <div class="flex items-center gap-px" ...attributes>
      {{#if this.hasEvents}}
        <input
          class="w-10 p-1 text-right border rounded dark:bg-primary-darkComponent dark:border-primary-darkBorderColor"
          value={{this.minValue}}
          aria-label={{if this.hasMultipleEvents "Minimum velocity" "Velocity"}}
          {{on "change" this.updateMinValue}}
        />
        {{#if this.hasMultipleEvents}}
          <span>-</span><input
            class="w-10 p-1 border rounded dark:bg-primary-darkComponent dark:border-primary-darkBorderColor"
            value={{this.maxValue}}
            aria-label="Maximum velocity"
            {{on "change" this.updateMaxValue}}
          />
        {{/if}}
      {{else}}
        <input
          class="w-10 p-1 text-right border rounded dark:bg-primary-darkComponent dark:border-primary-darkBorderColor"
          value={{@defaultValue}}
          aria-label="Default velocity"
          {{on "change" this.setDefaultValue}}
        />
      {{/if}}
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    VelocityValues: typeof VelocityValues;
  }
}
